import { WalletType, WalletTypesConfig } from "@cede/types";
import { CEDE_CEXS } from "../../cexs";

/**
 * For exchanges missing from the three wallet-type maps below, default to a
 * single unified SPOT account. This covers every DEX integration we ship —
 * Hyperliquid, Paradex, GRVT, ZeroOne, Extended, Hibachi, Decibel, Hotstuff,
 * Rise and any HIP-3 platform: they all expose one account with no
 * wallet-type distinction. Without this default, every new integration would
 * need 3 boilerplate lines here just to be queryable for balances.
 *
 * Multi-wallet exchanges (CEXes with FUNDING/SPOT/MARGIN splits like Binance,
 * KuCoin, OKX, Bybit) MUST stay explicit — they override the default by
 * including their entry in the map below.
 */
function applySpotDefaults(config: WalletTypesConfig): WalletTypesConfig {
  const result: WalletTypesConfig = {
    send_wallet_type: { ...config.send_wallet_type },
    transfer_wallet_types: { ...config.transfer_wallet_types },
    external_to_internal_wallet_types: { ...config.external_to_internal_wallet_types },
    internal_to_external_wallet_types: { ...config.internal_to_external_wallet_types },
    external_wallet_types_parameters: { ...config.external_wallet_types_parameters },
  };
  for (const exchangeId of Object.values(CEDE_CEXS)) {
    // Skip exchanges that explicitly opted out of transfers (an empty array
    // in `transfer_wallet_types` is a deliberate "no transfers supported"
    // signal — e.g. KRAKEN_FUTURES). Auto-filling `send_wallet_type = SPOT`
    // for those would contradict the empty transfer list and break the
    // walletTypes validation invariant ("every send_wallet_type must be in
    // transfer_wallet_types for the same exchange").
    const explicitlyNoTransfers =
      exchangeId in result.transfer_wallet_types && result.transfer_wallet_types[exchangeId].length === 0;
    if (explicitlyNoTransfers) continue;

    if (!(exchangeId in result.send_wallet_type)) {
      result.send_wallet_type[exchangeId] = WalletType.SPOT;
    }
    if (!(exchangeId in result.transfer_wallet_types)) {
      result.transfer_wallet_types[exchangeId] = [WalletType.SPOT];
    }
    if (!(exchangeId in result.external_wallet_types_parameters)) {
      result.external_wallet_types_parameters[exchangeId] = { [WalletType.SPOT]: [{}] };
    }
  }
  return result;
}

const RAW_COMMON_CONFIG: WalletTypesConfig = {
  send_wallet_type: {
    [CEDE_CEXS.OKX]: WalletType.FUNDING,
    [CEDE_CEXS.BINANCE]: WalletType.SPOT,
    [CEDE_CEXS.KUCOIN]: WalletType.FUNDING,
    [CEDE_CEXS.BYBIT]: WalletType.FUNDING,
    [CEDE_CEXS.BITFINEX]: WalletType.SPOT,
    [CEDE_CEXS.COINBASE]: WalletType.SPOT,
    [CEDE_CEXS.KRAKEN]: WalletType.SPOT,
    [CEDE_CEXS.GATEIO]: WalletType.SPOT,
    [CEDE_CEXS.HTX]: WalletType.SPOT,
    [CEDE_CEXS.BITGET]: WalletType.SPOT,
    [CEDE_CEXS.MEXC]: WalletType.SPOT,
    [CEDE_CEXS.BITSTAMP]: WalletType.SPOT,
    [CEDE_CEXS.COINBASE_EXCHANGE]: WalletType.SPOT,
    [CEDE_CEXS.CRYPTOCOM]: WalletType.SPOT,
    [CEDE_CEXS.BITHUMB]: WalletType.SPOT,
    [CEDE_CEXS.HYPERLIQUID]: WalletType.SPOT,
    [CEDE_CEXS.TRADEXYZ]: WalletType.SPOT,
    [CEDE_CEXS.PARADEX]: WalletType.SPOT,
    [CEDE_CEXS.FLX]: WalletType.SPOT,
    [CEDE_CEXS.VNTL]: WalletType.SPOT,
    [CEDE_CEXS.HYNA]: WalletType.SPOT,
    [CEDE_CEXS.KM]: WalletType.SPOT,
    [CEDE_CEXS.CASH]: WalletType.SPOT,
    [CEDE_CEXS.GRVT]: WalletType.SPOT,
    [CEDE_CEXS.ZEROONE]: WalletType.SPOT,
    [CEDE_CEXS.EXTENDED]: WalletType.SPOT,
    [CEDE_CEXS.HIBACHI]: WalletType.SPOT,
    [CEDE_CEXS.DECIBEL]: WalletType.SPOT,
    [CEDE_CEXS.HOTSTUFF]: WalletType.SPOT,
    [CEDE_CEXS.KATANA]: WalletType.USD_M_FUTURES,
    // LBank is integrated as USDT-margined perps only (SwapU) — settles under USD_M_FUTURES.
    [CEDE_CEXS.LBANK]: WalletType.USD_M_FUTURES,
    // Rise — see applySpotDefaults
  },
  transfer_wallet_types: {
    [CEDE_CEXS.OKX]: [WalletType.FUNDING, WalletType.SPOT],
    [CEDE_CEXS.BINANCE]: [WalletType.SPOT, WalletType.FUNDING],
    [CEDE_CEXS.KUCOIN]: [WalletType.FUNDING, WalletType.SPOT],
    [CEDE_CEXS.BYBIT]: [WalletType.FUNDING, WalletType.UNIFIED],
    [CEDE_CEXS.BITFINEX]: [WalletType.SPOT],
    [CEDE_CEXS.COINBASE]: [WalletType.SPOT],
    [CEDE_CEXS.KRAKEN]: [WalletType.SPOT],
    [CEDE_CEXS.KRAKEN_FUTURES]: [],
    [CEDE_CEXS.GATEIO]: [WalletType.SPOT],
    [CEDE_CEXS.HTX]: [WalletType.SPOT],
    [CEDE_CEXS.BITGET]: [WalletType.SPOT],
    [CEDE_CEXS.MEXC]: [WalletType.SPOT],
    [CEDE_CEXS.BITSTAMP]: [WalletType.SPOT],
    [CEDE_CEXS.COINBASE_EXCHANGE]: [WalletType.SPOT],
    [CEDE_CEXS.BITMART]: [WalletType.SPOT],
    [CEDE_CEXS.CRYPTOCOM]: [WalletType.SPOT],
    [CEDE_CEXS.BITHUMB]: [WalletType.SPOT],
    [CEDE_CEXS.HYPERLIQUID]: [WalletType.SPOT],
    [CEDE_CEXS.TRADEXYZ]: [WalletType.SPOT],
    [CEDE_CEXS.PARADEX]: [WalletType.SPOT],
    [CEDE_CEXS.FLX]: [WalletType.SPOT],
    [CEDE_CEXS.VNTL]: [WalletType.SPOT],
    [CEDE_CEXS.HYNA]: [WalletType.SPOT],
    [CEDE_CEXS.KM]: [WalletType.SPOT],
    [CEDE_CEXS.CASH]: [WalletType.SPOT],
    [CEDE_CEXS.GRVT]: [WalletType.SPOT],
    [CEDE_CEXS.ZEROONE]: [WalletType.SPOT],
    [CEDE_CEXS.EXTENDED]: [WalletType.SPOT],
    [CEDE_CEXS.HIBACHI]: [WalletType.SPOT],
    [CEDE_CEXS.DECIBEL]: [WalletType.SPOT],
    [CEDE_CEXS.HOTSTUFF]: [WalletType.SPOT],
    [CEDE_CEXS.KATANA]: [WalletType.USD_M_FUTURES],
    [CEDE_CEXS.LBANK]: [WalletType.USD_M_FUTURES],
    // Rise — see applySpotDefaults
  },
  external_to_internal_wallet_types: {
    [CEDE_CEXS.BINANCE]: {
      spot: WalletType.SPOT,
    },
    [CEDE_CEXS.BITFINEX]: {
      spot: WalletType.SPOT,
    },
    [CEDE_CEXS.BITGET]: {
      spot: WalletType.SPOT,
      funding: WalletType.FUNDING,
    },
    [CEDE_CEXS.BYBIT]: {
      UNIFIED: WalletType.SPOT,
      FUND: WalletType.FUNDING,
    },
    [CEDE_CEXS.GATEIO]: {
      spot: WalletType.SPOT,
      margin: WalletType.ISOLATED_MARGIN,
    },
    [CEDE_CEXS.KUCOIN]: {
      trade: WalletType.SPOT,
      main: WalletType.FUNDING,
      margin: WalletType.ISOLATED_MARGIN,
      earn: WalletType.EARN,
    },
    [CEDE_CEXS.MEXC]: {
      spot: WalletType.SPOT,
    },
    [CEDE_CEXS.BITSTAMP]: {
      spot: WalletType.SPOT,
    },
    [CEDE_CEXS.HTX]: {
      spot: WalletType.SPOT,
      margin: WalletType.CROSS_MARGIN,
    },
    [CEDE_CEXS.OKX]: {
      trading: WalletType.SPOT,
      funding: WalletType.FUNDING,
    },
    [CEDE_CEXS.COINBASE_EXCHANGE]: {
      spot: WalletType.SPOT,
    },
    [CEDE_CEXS.BITMART]: {
      spot: WalletType.SPOT,
    },
    [CEDE_CEXS.BITHUMB]: {
      spot: WalletType.SPOT,
    },
  },
  internal_to_external_wallet_types: {
    [CEDE_CEXS.KRAKEN]: {
      [WalletType.SPOT]: "spot",
      [WalletType.EARN]: "earn",
    },
    [CEDE_CEXS.BITFINEX]: {
      [WalletType.SPOT]: "exchange",
      [WalletType.CROSS_MARGIN]: "margin",
      [WalletType.DERIVATIVES]: "derivatives",
      [WalletType.FUNDING]: "funding",
    },
    [CEDE_CEXS.BYBIT]: {
      [WalletType.FUNDING]: "fund",
      [WalletType.SPOT]: "spot",
      [WalletType.UNIFIED]: "unified",
    },
    [CEDE_CEXS.KUCOIN]: {
      [WalletType.SPOT]: "trade",
      [WalletType.ISOLATED_MARGIN]: "margin",
      [WalletType.FUNDING]: "main",
      [WalletType.EARN]: "earn",
    },
    [CEDE_CEXS.OKX]: {
      [WalletType.FUNDING]: "funding",
      [WalletType.SPOT]: "trading",
      [WalletType.CROSS_MARGIN]: "margin",
      [WalletType.COIN_M_SWAP]: "swap",
      [WalletType.OPTIONS]: "option",
    },
    [CEDE_CEXS.BITGET]: {
      [WalletType.FUNDING]: "funding",
      [WalletType.SPOT]: "spot",
    },
    [CEDE_CEXS.MEXC]: {
      [WalletType.SPOT]: "spot",
    },
    [CEDE_CEXS.GATEIO]: {
      [WalletType.SPOT]: "spot",
      [WalletType.ISOLATED_MARGIN]: "margin",
      [WalletType.CROSS_MARGIN]: "margin",
    },
    [CEDE_CEXS.COINBASE_EXCHANGE]: {
      [WalletType.SPOT]: "spot",
    },
    [CEDE_CEXS.BITHUMB]: {
      [WalletType.SPOT]: "spot",
    },
  },
  external_wallet_types_parameters: {
    [CEDE_CEXS.BITGET]: {
      [WalletType.FUNDING]: [
        {
          type: "funding",
        },
      ],
      [WalletType.SPOT]: [
        {
          type: "spot",
        },
      ],
      [WalletType.CROSS_MARGIN]: [
        {
          type: "spot",
          marginMode: "cross",
        },
      ],
      [WalletType.ISOLATED_MARGIN]: [
        {
          type: "spot",
          marginMode: "isolated",
        },
      ],
      [WalletType.COIN_M_FUTURES]: [
        {
          type: "swap",
          productType: "COIN-FUTURES",
        },
      ],
      [WalletType.USDT_M_FUTURES]: [
        {
          type: "swap",
          productType: "USDT-FUTURES",
        },
      ],
      [WalletType.USDC_M_FUTURES]: [
        {
          type: "swap",
          productType: "USDC-FUTURES",
        },
      ],
      [WalletType.EARN]: [
        {
          type: "earn",
        },
      ],
      [WalletType.USD_M_FUTURES]: [
        {
          type: "swap",
          productType: "USDT-FUTURES",
        },
        {
          type: "swap",
          productType: "USDC-FUTURES",
        },
      ],
    },
    [CEDE_CEXS.BITFINEX]: {
      [WalletType.SPOT]: [{ type: "exchange" }],
      [WalletType.CROSS_MARGIN]: [{ type: "margin" }],
      [WalletType.DERIVATIVES]: [{ type: "derivatives" }],
      [WalletType.FUNDING]: [{ type: "funding" }],
    },
    [CEDE_CEXS.COINBASE]: {
      [WalletType.SPOT]: [{}], // coinbase only have 1 wallet type, spot will be used in ccxt
    },
    [CEDE_CEXS.COINBASE_EXCHANGE]: {
      [WalletType.SPOT]: [{}], // coinbaseexchange only have 1 wallet type, spot will be used in ccxt
    },
    [CEDE_CEXS.HTX]: {
      [WalletType.SPOT]: [{}],
      [WalletType.CROSS_MARGIN]: [
        {
          type: "spot",
          marginMode: "cross",
        },
      ],
      [WalletType.ISOLATED_MARGIN]: [
        {
          type: "spot",
          marginMode: "isolated",
        },
      ],
      [WalletType.USD_M_FUTURES]: [{ type: "future", unified: true }],
      [WalletType.COIN_M_FUTURES]: [{ type: "future", subType: "inverse" }],
      [WalletType.COIN_M_SWAP]: [{ type: "swap", subType: "inverse" }],
    },
    [CEDE_CEXS.KRAKEN]: {
      [WalletType.SPOT]: [{}],
      [WalletType.EARN]: [
        {
          type: "earn",
        },
      ],
    },
    [CEDE_CEXS.KRAKEN_FUTURES]: {
      [WalletType.FUTURES]: [
        {
          type: "futures",
        },
      ],
    },
    [CEDE_CEXS.BYBIT]: {
      [WalletType.FUNDING]: [{ type: "fund" }],
      [WalletType.UNIFIED]: [{ type: "unified" }],
      [WalletType.FUTURES]: [{ type: "unified", future: true }],
    },
    [CEDE_CEXS.BINANCE]: {
      [WalletType.FUNDING]: [
        {
          type: "funding",
        },
      ],
      [WalletType.EARN]: [
        {
          type: "savings",
        },
      ],
      [WalletType.COIN_M_FUTURES]: [
        {
          // Coin M
          type: "delivery",
        },
      ],
      [WalletType.USD_M_FUTURES]: [
        {
          // USD-M
          type: "future",
        },
      ],
      [WalletType.CROSS_MARGIN]: [
        {
          type: "margin",
          marginMode: "cross",
        },
      ],
      [WalletType.ISOLATED_MARGIN]: [
        {
          type: "margin",
          marginMode: "isolated",
        },
      ],
      [WalletType.SPOT]: [{}],
      [WalletType.OPTIONS]: [
        {
          type: "option",
        },
      ],
    },
    [CEDE_CEXS.GATEIO]: {
      [WalletType.SPOT]: [
        {
          type: "spot",
          marginMode: "",
        },
      ],
      [WalletType.ISOLATED_MARGIN]: [
        {
          type: "spot",
          marginMode: "margin",
        },
      ],
      [WalletType.CROSS_MARGIN]: [
        {
          type: "spot",
          marginMode: "cross_margin",
        },
      ],
      [WalletType.OPTIONS]: [
        {
          type: "option",
        },
      ],
      [WalletType.COIN_M_SWAP]: [
        {
          type: "swap",
        },
      ],
      [WalletType.COIN_M_FUTURES]: [
        {
          type: "future",
        },
      ],
    },
    [CEDE_CEXS.OKX]: {
      [WalletType.FUNDING]: [
        {
          type: "funding",
        },
      ],
      [WalletType.SPOT]: [
        {
          type: "trading",
        },
      ],
      [WalletType.EARN]: [
        {
          type: "savings",
        },
      ],
    },
    [CEDE_CEXS.KUCOIN]: {
      [WalletType.SPOT]: [
        {
          type: "trade",
        },
      ],
      [WalletType.ISOLATED_MARGIN]: [
        {
          type: "margin",
          marginMode: "isolated",
        },
      ],
      [WalletType.CROSS_MARGIN]: [
        {
          type: "margin",
          marginMode: "cross",
        },
      ],
      [WalletType.FUNDING]: [
        {
          type: "main",
        },
      ],
      [WalletType.EARN]: [
        {
          type: "earn",
        },
      ],
      [WalletType.FUTURES]: [
        {
          type: WalletType.FUTURES,
        },
      ],
      [WalletType.OPTIONS]: [
        {
          type: WalletType.OPTIONS,
        },
      ],
    },
    [CEDE_CEXS.BITMART]: {
      [WalletType.SPOT]: [
        {
          type: "spot",
        },
      ],
      [WalletType.USD_M_FUTURES]: [
        {
          type: "usd-m",
        },
      ],
      [WalletType.COIN_M_FUTURES]: [
        {
          type: "coin-m",
        },
      ],
      [WalletType.ISOLATED_MARGIN]: [{ type: "margin" }],
    },
    [CEDE_CEXS.BITSTAMP]: {
      [WalletType.SPOT]: [
        {
          type: "spot",
        },
      ],
      [WalletType.EARN]: [
        {
          type: "earn",
        },
      ],
    },
    [CEDE_CEXS.MEXC]: {
      [WalletType.SPOT]: [
        {
          type: "spot",
        },
      ],
      [WalletType.FUTURES]: [
        {
          type: WalletType.FUTURES,
        },
      ],
    },
    [CEDE_CEXS.CRYPTOCOM]: {
      [WalletType.SPOT]: [
        {
          type: "spot",
        },
      ],
      [WalletType.USD_M_FUTURES]: [
        {
          type: "future",
        },
      ],
    },
    [CEDE_CEXS.BITHUMB]: {
      [WalletType.SPOT]: [
        {
          type: "spot",
        },
      ],
    },
    [CEDE_CEXS.HYPERLIQUID]: {
      [WalletType.SPOT]: [{ type: "spot" }], // Explicit type to override defaultType "swap"
      [WalletType.USD_M_FUTURES]: [{ type: "swap" }],
    },
    [CEDE_CEXS.TRADEXYZ]: {
      [WalletType.SPOT]: [{ type: "spot" }], // Explicit type to override defaultType "swap"
    },
    [CEDE_CEXS.FLX]: {
      [WalletType.SPOT]: [{}], // Felix (HIP-3 DEX) has a unified account
    },
    [CEDE_CEXS.VNTL]: {
      [WalletType.SPOT]: [{}], // Ventuals (HIP-3 DEX) has a unified account
    },
    [CEDE_CEXS.HYNA]: {
      [WalletType.SPOT]: [{}], // HyENA (HIP-3 DEX) has a unified account
    },
    [CEDE_CEXS.KM]: {
      [WalletType.SPOT]: [{}], // Markets by Kinetiq (HIP-3 DEX) has a unified account
    },
    [CEDE_CEXS.CASH]: {
      [WalletType.SPOT]: [{}], // Dream Cash (HIP-3 DEX) has a unified account
    },
    [CEDE_CEXS.PARADEX]: {
      [WalletType.SPOT]: [{}], // Paradex has a unified account
    },
    [CEDE_CEXS.GRVT]: {
      [WalletType.SPOT]: [{}], // GRVT has a unified account
    },
    [CEDE_CEXS.ZEROONE]: {
      [WalletType.SPOT]: [{}], // ZeroOne has a unified account
    },
    [CEDE_CEXS.EXTENDED]: {
      [WalletType.SPOT]: [{}], // Extended has a unified account
    },
    [CEDE_CEXS.HIBACHI]: {
      [WalletType.SPOT]: [{}], // Hibachi has a unified account
    },
    [CEDE_CEXS.DECIBEL]: {
      [WalletType.SPOT]: [{}], // Decibel has a unified account (USDC-margined)
    },
    [CEDE_CEXS.HOTSTUFF]: {
      [WalletType.SPOT]: [{}], // Hotstuff has a unified account
    },
    [CEDE_CEXS.KATANA]: {
      [WalletType.USD_M_FUTURES]: [{}], // Katana has a unified perp margin account (VBUSDC)
    },
    [CEDE_CEXS.LBANK]: {
      [WalletType.USD_M_FUTURES]: [{}], // LBank SwapU is a unified USDT perp margin account
    },
    // Rise (and any future DEX with a single unified account) is covered by
    // the SPOT default — see `applySpotDefaults` at the top of this file.
  },
};

export const WALLET_TYPES_COMMON_CONFIG: WalletTypesConfig = applySpotDefaults(RAW_COMMON_CONFIG);
